/**
 * Single-request gzip upload for the Capacity Planning deliveries file
 * (ADR-0004, superseding the resumable upload of ADR-0002): the browser
 * compresses the whole file with `fflate`, then posts it in one
 * `multipart/form-data` request with `axios`. The API decompresses, parses
 * and stores the Run and its Deliveries in one transaction, so the response
 * carries the finished Run.
 */
import axios from 'axios';
import { gzip } from 'fflate';
import {
  MAX_REQUEST_SIZE_BYTES,
  compressedTooLargeMessage,
} from './capacityPlanning';

export interface CapacityPlanUploadRequest {
  /** Absolute URL of `POST api/capacity-plans/runs`. */
  endpoint: string;
  file: File;
  /** Run Settings as JSON, matching the API's `RunSettingsInput`. */
  settings: string;
  /** Guid of the Agency; omitted entirely for a Network Run. */
  agencyId?: string;
  label?: string;
  /** Authorization and `X-Tenant-Country`, as the Apollo link sends them. */
  headers?: Record<string, string>;
  onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
  signal?: AbortSignal;
}

export interface CapacityPlanUploadResult {
  /** Guid of the Run the upload created. */
  runId: string;
  totalRows: number;
}

export class CapacityPlanUploadAbortedError extends Error {
  constructor() {
    super('The upload was cancelled.');
    this.name = 'CapacityPlanUploadAbortedError';
  }
}

/** The `.message` of a `{ code, message }` refusal body, when it has one. */
const messageOf = (value: unknown): string | undefined => {
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { message?: unknown }).message === 'string'
  ) {
    return (value as { message: string }).message;
  }
  return undefined;
};

/**
 * The API answers a refusal as one plain-text message, an array of
 * FluentValidation messages, or a `{ code, message }` object (403 scope
 * refusals, e.g. `NetworkRunRequiresHq`), so every shape is turned into one
 * line. Those messages name columns, headers, limits and refusal codes only -
 * never a row's content. `axios` already parses a JSON array or object body
 * for us, but a plain-text body that still looks like `["…"]` or `{"…"}` is
 * handled too.
 */
export const readApiError = (body: unknown): string => {
  if (Array.isArray(body)) {
    return body.join(' ');
  }

  if (typeof body === 'object' && body !== null) {
    return messageOf(body) ?? 'The upload failed.';
  }

  if (typeof body !== 'string') {
    return 'The upload failed.';
  }

  const trimmed = body.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
    return trimmed;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.join(' ');
    }
    return messageOf(parsed) ?? trimmed;
  } catch {
    return trimmed;
  }
};

/** `File.arrayBuffer()` alone would do, but `FileReader` also works on jsdom. */
const readAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () =>
      reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsArrayBuffer(file);
  });

/** Wraps fflate's callback-style async gzip in a Promise, at a fixed ratio. */
const gzipAsync = (data: Uint8Array): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    gzip(data, { level: 6 }, (error, result) => {
      if (error) {
        reject(error);
      } else {
        resolve(result);
      }
    });
  });

/**
 * Compresses the file, then posts it as one multipart request. Progress has
 * two phases: compression is reported once as zero bytes of the file's
 * total (fflate's single-shot API gives no finer-grained callback), then the
 * transfer percentage comes from `axios`'s own upload progress.
 */
export async function uploadCapacityPlanFile(
  request: CapacityPlanUploadRequest,
): Promise<CapacityPlanUploadResult> {
  const {
    endpoint,
    file,
    settings,
    agencyId,
    label,
    headers,
    onProgress,
    signal,
  } = request;

  if (signal?.aborted) {
    throw new CapacityPlanUploadAbortedError();
  }

  onProgress?.(0, file.size);

  const bytes = new Uint8Array(await readAsArrayBuffer(file));
  const compressed = await gzipAsync(bytes);

  if (signal?.aborted) {
    throw new CapacityPlanUploadAbortedError();
  }

  // Checked here rather than on the picked file: the request body is the
  // gzipped file, and how well a file compresses is only known now.
  if (compressed.length > MAX_REQUEST_SIZE_BYTES) {
    throw new Error(compressedTooLargeMessage());
  }

  const formData = new FormData();
  formData.append('file', new Blob([compressed]), `${file.name}.gz`);
  formData.append('settings', settings);
  if (agencyId) {
    formData.append('agencyId', agencyId);
  }
  if (label) {
    formData.append('label', label);
  }

  try {
    const response = await axios.post<CapacityPlanUploadResult>(
      endpoint,
      formData,
      {
        headers,
        signal,
        onUploadProgress: (event) =>
          onProgress?.(event.loaded, event.total ?? event.loaded),
      },
    );

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new CapacityPlanUploadAbortedError();
    }

    if (axios.isAxiosError(error) && error.response) {
      throw new Error(readApiError(error.response.data));
    }

    throw error;
  }
}
